/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorStyle,
  useColorMode,
  Select,
  Option,
  Button,
} from '@tonic-one/react';
import {
  TimeOfClickActions,
  TimeOfClickActionsEnum,
  TimeOfClickKeys,
} from '../utils/types';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import { FormLabel } from 'src/components/Common/FormComponent';
import { useSiderWidthStore } from 'src/models/useSiderWidthModel';
import { useOrgStore } from '../../../models/useOrgModel';
import { useTranslation } from 'react-i18next';
import { GlobalApi } from '@Apis';
import ErrorAlert from 'src/components/Common/ErrorAlert';
import CSpinner from 'src/components/CSpinner';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

export interface TimeOfClickActionsStatus {
  dangerous: TimeOfClickActions;
  highlySuspicious: TimeOfClickActions;
  suspicious: TimeOfClickActions;
  untested: TimeOfClickActions;
}
const TimeOfClickProtectionSettingsSelections = React.memo(
  ({
    title,
    defaultValue,
    setTimeOfClickProtectionSetting,
    disabled = false,
  }: {
    title: string;
    defaultValue: TimeOfClickActions;
    setTimeOfClickProtectionSetting: (state: TimeOfClickActions) => void;
    disabled?: boolean;
  }) => {
    const { t } = useTranslation();
    const timeOfClickActionList: TimeOfClickActions[] = [
      'Block',
      'Warn',
      'Allow',
    ];
    const timeOfClickActionsListMap = {
      Block: 0,
      Warn: 1,
      Allow: 2,
    };
    useWhyDidYouUpdate('Time Of Click Protection Settings Selections', {
      title,
      defaultValue,
      setTimeOfClickProtectionSetting,
      disabled,
    });
    return (
      <Flex alignItems="center">
        <FormLabel label={title} width="200px" />
        <Select
          onChange={async (e: React.ChangeEvent<HTMLSelectElement>) => {
            setTimeOfClickProtectionSetting(
              timeOfClickActionList[Number(e.target.value)]
            );
          }}
          width="265px"
          mr="25px"
          value={timeOfClickActionsListMap[defaultValue]}
          disabled={disabled}
        >
          <Option value={TimeOfClickActionsEnum.block}>
            {t('globalSetting.timeOfClick.actions.block')}
          </Option>
          <Option value={TimeOfClickActionsEnum.warn}>
            {t('globalSetting.timeOfClick.actions.warm')}
          </Option>
          <Option value={TimeOfClickActionsEnum.allow}>
            {t('globalSetting.timeOfClick.actions.allow')}
          </Option>
        </Select>
      </Flex>
    );
  }
);

const TimeOfClickProtectionSettings = React.memo(() => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { siderWidth } = useSiderWidthStore();
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const timeOfClickStatus = {
    dangerous: TimeOfClickKeys.allow,
    highlySuspicious: TimeOfClickKeys.allow,
    suspicious: TimeOfClickKeys.allow,
    untested: TimeOfClickKeys.allow,
  };
  const { orgId } = useOrgStore();
  const [dangerousSetting, setDangerousSetting] = useState<TimeOfClickActions>(
    timeOfClickStatus.dangerous
  );
  const [highSuspiciousSetting, setHighSuspiciousSetting] =
    useState<TimeOfClickActions>(timeOfClickStatus.highlySuspicious);
  const [suspiciousSetting, setSuspiciousSetting] =
    useState<TimeOfClickActions>(timeOfClickStatus.suspicious);
  const [untestedSetting, setUntestedSetting] = useState<TimeOfClickActions>(
    timeOfClickStatus.untested
  );
  const onSave = async () => {
    try {
      setLoading(true);
      await GlobalApi.updateTimeOfClickStatus({
        dangerous: dangerousSetting,
        highlySuspicious: highSuspiciousSetting,
        suspicious: suspiciousSetting,
        untested: untestedSetting,
      });
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    (async function doCall() {
      try {
        setLoading(true);
        const timeOfClickStatus: TimeOfClickActionsStatus = (
          await GlobalApi.getTimeOfClickStatus()
        ).data;
        setDangerousSetting(timeOfClickStatus.dangerous);
        setHighSuspiciousSetting(timeOfClickStatus.highlySuspicious);
        setSuspiciousSetting(timeOfClickStatus.suspicious);
        setUntestedSetting(timeOfClickStatus.untested);
      } catch (err) {
        setErrorMsg(t('common.apiException.internalError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);
  useWhyDidYouUpdate('TimeOfClickProtectionSettings Base', {
    withEditPermission,
    onSave,
  });
  return (
    <Box px="4x">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      <Flex m="16px 0px">
        <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
          {t('globalSetting.timeOfClick.title')}
        </Text>
      </Flex>
      <Flex m="16px 0px">
        <Text
          color={colorStyle.color.secondary}
          lineHeight="lg"
          maxWidth={1000}
        >
          {t('globalSetting.timeOfClick.desc')}
        </Text>
      </Flex>
      <Box>
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('policy.table.fields.actions')}
          </Text>
        </Flex>
        <Flex pt="8px">
          <TimeOfClickProtectionSettingsSelections
            title={t('globalSetting.timeOfClick.dangerous')}
            defaultValue={dangerousSetting}
            setTimeOfClickProtectionSetting={setDangerousSetting}
            disabled={!withEditPermission}
          />
        </Flex>
        <Flex pt="8px">
          <TimeOfClickProtectionSettingsSelections
            title={t('globalSetting.timeOfClick.highlySuspicious')}
            defaultValue={highSuspiciousSetting}
            setTimeOfClickProtectionSetting={setHighSuspiciousSetting}
            disabled={!withEditPermission}
          />
        </Flex>
        <Flex pt="8px">
          <TimeOfClickProtectionSettingsSelections
            title={t('globalSetting.timeOfClick.suspicious')}
            defaultValue={suspiciousSetting}
            setTimeOfClickProtectionSetting={setSuspiciousSetting}
            disabled={!withEditPermission}
          />
        </Flex>
        <Flex pt="8px">
          <TimeOfClickProtectionSettingsSelections
            title={t('globalSetting.timeOfClick.untested')}
            defaultValue={untestedSetting}
            setTimeOfClickProtectionSetting={setUntestedSetting}
            disabled={!withEditPermission}
          />
        </Flex>
      </Box>
      <Flex
        position="fixed"
        pl="5x"
        bottom={0}
        left={270 + siderWidth}
        right={0}
        background={colorStyle.background.secondary}
        height={64}
        align="center"
        justify="flex-start"
      >
        <Button
          variant="primary"
          onClick={onSave}
          disabled={!withEditPermission}
        >
          {t('common.button.save')}
        </Button>
      </Flex>
    </Box>
  );
});
export default TimeOfClickProtectionSettings;
